import React from 'react';
import { Table, Badge, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import GiftLoader from '../../common/GiftLoader';

interface Issue {
  id?: string;
  _id?: string;
  ticketId?: string;
  subject?: string;
  title?: string;
  description: string;
  status: 'Open' | 'In Progress' | 'Resolved';
  submittedOn?: string;
  createdAt?: string;
  createdDate?: string;
  replies?: any[];
  conversation?: any[];
}

interface TicketTableProps {
  issues: Issue[];
  loading: boolean;
  onTicketClick: (ticketId: string) => void;
  onSubmitNewTicket: () => void;
}

const TicketTable: React.FC<TicketTableProps> = ({
  issues,
  loading,
  onTicketClick,
  onSubmitNewTicket
}) => {
  const getStatusBadge = (status: Issue['status']) => {
    if (!status) {
      return (
        <span className="status-open">
          Unknown
        </span>
      );
    }

    const statusLower = status?.toLowerCase().trim();

    switch (statusLower) {
      case 'open':
        return (
          <span className="status-open">
            Open
          </span>
        );
      case 'in progress':
      case 'inprogress':
      case 'in-progress':
        return (
          <span className="status-in-progress">
            In Progress
          </span>
        );
      case 'resolved':
      case 'closed':
      case 'completed':
        return (
          <span className="status-resolved">
            Resolved
          </span>
        );
      default:
        // Always use custom styling, never Bootstrap Badge
        return (
          <span className="status-open">
            {status}
          </span>
        );
    }
  };

  const handleRowClick = (issue: Issue) => {
    // Try different possible ID field names
    const ticketId = issue.id || issue._id || issue.ticketId;

    if (ticketId) {
      onTicketClick(ticketId);
    } else {
      console.error('No valid ticket ID found in issue data');
    }
  };

  return (
    <div className="issues-section">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 style={{ color: '#3957A0', fontWeight: 'bold' }}>
          All Tickets
        </h4>
        <Button
          className="main-bttn btn-primary w-auto"
          onClick={onSubmitNewTicket}
        >
          Submit an Issue
        </Button>
      </div>

      <div className="table-container">
        {loading ? (
          <GiftLoader
            text="Loading your support tickets..."
            size="medium"
            showText={true}
          />
        ) : (
          <Table responsive hover className="tickets-table">
            <thead>
              <tr>
                <th>Issue ID</th>
                <th>Subject</th>
                <th>Submitted On</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {issues.length > 0 ? (
                issues.map((issue) => {
                  const ticketId = issue.id || issue._id || issue.ticketId;
                  return (
                    <tr
                      key={ticketId}
                      onClick={() => handleRowClick(issue)}
                      className="clickable-row"
                      style={{
                        cursor: 'pointer'
                      }}
                    >
                      <td>
                        <Link
                          to={`/help-support/ticket/${ticketId}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none'
                          }}
                        >
                          {issue.ticketId || issue.id || issue._id}
                        </Link>
                      </td>
                      <td>
                        <Link
                          to={`/help-support/ticket/${ticketId}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none'
                          }}
                        >
                          {issue.subject || issue.title}
                        </Link>
                      </td>
                      <td>
                        {issue.submittedOn ||
                         (issue.createdDate ? new Date(issue.createdDate).toLocaleString() :
                          issue.createdAt ? new Date(issue.createdAt).toLocaleString() : '')}
                      </td>
                      <td>{getStatusBadge(issue.status)}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={4} className="text-center py-4">
                    <div className="text-muted">
                      <p>No tickets found.</p>
                      <p>Click "Submit an Issue" to create your first ticket.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )}
      </div>
    </div>
  );
};

export default TicketTable;